import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, inr, fmtErr } from "@/lib/api";
import { Leaf, Flame, ArrowLeft, ShoppingCart, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

export default function GuestMenuPage() {
  const [cats, setCats] = useState([]);
  const [cart, setCart] = useState({});
  const [placing, setPlacing] = useState(false);
  const [instr, setInstr] = useState("");

  useEffect(() => {
    http.get("/guest/menu").then((r) => setCats(r.data.categories));
    const c = JSON.parse(localStorage.getItem("cart") || "{}");
    setCart(c);
  }, []);
  useEffect(() => { localStorage.setItem("cart", JSON.stringify(cart)); }, [cart]);

  const items = cats.flatMap((c) => c.items || []);
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartTotal = Object.entries(cart).reduce((a, [id, q]) => {
    const it = items.find((x) => x.id === id);
    return a + (it ? it.price * q : 0);
  }, 0);

  const add = (id) => setCart((p) => ({ ...p, [id]: (p[id] || 0) + 1 }));
  const dec = (id) => setCart((p) => { const q = (p[id] || 0) - 1; const n = { ...p }; if (q <= 0) delete n[id]; else n[id] = q; return n; });

  const place = async () => {
    if (cartCount === 0) return;
    setPlacing(true);
    try {
      const body = {
        items: Object.entries(cart).map(([id, q]) => ({ food_item_id: id, quantity: q })),
        special_instruction: instr,
        idempotency_key: `order-${Date.now()}`,
      };
      const r = await http.post("/guest/orders", body);
      toast.success(`Order ${r.data.order_number} placed`);
      setCart({}); setInstr("");
      window.location.href = "/guest/orders";
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setPlacing(false); }
  };

  return (
    <div className="min-h-screen pb-40" style={{ background: "var(--surface)" }}>
      <div className="max-w-lg mx-auto px-4 pt-6">
        <Link to="/guest/dashboard" data-testid="menu-back" className="flex items-center gap-1 text-sm mb-4" style={{ color: "var(--muted)" }}><ArrowLeft size={16} /> Back</Link>
        <div className="eyebrow mb-1">Kitchen</div>
        <h1 className="font-serif text-4xl mb-6">In-Villa Menu</h1>

        {cats.map((c) => (
          <div key={c.id} className="mb-8" data-testid={`menu-category-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
            <h2 className="font-serif text-2xl mb-3">{c.name}</h2>
            <div className="space-y-3">
              {(c.items || []).map((it) => (
                <div key={it.id} className="card flex gap-3" style={{ padding: 14 }}>
                  <img src={it.image} alt={it.name} className="w-20 h-20 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {it.veg ? <Leaf size={12} style={{ color: "#2A4436" }} /> : <span className="w-3 h-3 rounded-sm" style={{ background: "#B22222" }} />}
                      {it.spicy_level > 0 && <Flame size={12} style={{ color: "var(--terracotta)" }} />}
                    </div>
                    <div className="font-serif text-lg leading-tight">{it.name}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{it.description}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="font-medium">{inr(it.price)}</div>
                      {cart[it.id] ? (
                        <div className="flex items-center gap-2">
                          <button data-testid={`menu-dec-${it.id}`} onClick={() => dec(it.id)} className="w-8 h-8 rounded-full border flex items-center justify-center"><Minus size={14} /></button>
                          <span data-testid={`menu-qty-${it.id}`} className="w-6 text-center">{cart[it.id]}</span>
                          <button data-testid={`menu-inc-${it.id}`} onClick={() => add(it.id)} className="w-8 h-8 rounded-full text-white flex items-center justify-center" style={{ background: "var(--terracotta)" }}><Plus size={14} /></button>
                        </div>
                      ) : (
                        <button data-testid={`menu-add-${it.id}`} onClick={() => add(it.id)} className="btn-outline" style={{ padding: "6px 14px" }}>Add</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0" style={{ background: "var(--card)", borderTop: "1px solid var(--border)", padding: 12 }}>
          <div className="max-w-lg mx-auto">
            <input data-testid="menu-instruction" placeholder="Kitchen instructions (optional)" value={instr} onChange={(e) => setInstr(e.target.value)} className="mb-2" />
            <button data-testid="menu-place-order" disabled={placing} onClick={place} className="btn-primary w-full flex items-center justify-center gap-2">
              <ShoppingCart size={18} /> Place order · {cartCount} item{cartCount > 1 ? "s" : ""} · {inr(cartTotal)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
